import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Loader2, Check, Tag, X as XIcon } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

// Razorpay script will be loaded only when payment is initiated
let razorpayLoaded = false;

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      razorpayLoaded = true;
      resolve(true);
      return;
    }
    
    if (razorpayLoaded) {
      // Script tag exists, wait for it to load
      const checkLoaded = setInterval(() => {
        if (window.Razorpay) {
          clearInterval(checkLoaded);
          resolve(true);
        }
      }, 100);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      razorpayLoaded = true;
      resolve(true);
    };
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
    razorpayLoaded = true; // Mark as loading to prevent duplicate script tags
  });
};

const PaymentModal = () => {
  const { user, updateUserProfile } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleShowModal = (event) => {
      setPlanDetails(event.detail);
      setOpen(true);
    };

    window.addEventListener('showPaymentModal', handleShowModal);
    return () => window.removeEventListener('showPaymentModal', handleShowModal);
  }, []);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && open && !processingPayment) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, processingPayment]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && open && !processingPayment) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, processingPayment]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/apply-coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: planDetails.planId,
          couponCode: couponCode.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setAppliedCoupon(data);
        toast({
          title: "Coupon Applied!",
          description: `You saved ₹${(data.discountAmount / 100).toFixed(2)}`,
        });
      } else {
        toast({
          title: "Invalid Coupon",
          description: data.detail || "This coupon code is not valid",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast({
        title: "Error",
        description: "Failed to apply coupon",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handleSavePhoneAndProceed = async () => {
    // Validate phone number
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid 10-digit phone number",
        variant: "destructive"
      });
      return;
    }

    setSavingPhone(true);
    try {
      // Update phone number in Firebase and backend
      console.log('📱 Saving phone number:', cleanPhone);
      await updateUserProfile({ phoneNumber: cleanPhone });
      
      console.log('✅ Phone number saved, proceeding with payment');
      
      toast({
        title: "Success",
        description: "Phone number saved successfully"
      });
      
      setShowPhoneDialog(false);
      setPhoneNumber('');
      setSavingPhone(false);
      
      // Proceed with payment immediately using the saved phone
      // Add a small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Trigger payment with the updated user context
      setProcessingPayment(true);
      proceedWithPayment(cleanPhone);
    } catch (error) {
      console.error('❌ Error saving phone number:', error);
      toast({
        title: "Error",
        description: "Failed to save phone number",
        variant: "destructive"
      });
      setSavingPhone(false);
    }
  };

  const handlePayment = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to continue",
        variant: "destructive"
      });
      return;
    }

    // Check if phone number exists
    if (!user.phoneNumber) {
      setShowPhoneDialog(true);
      return;
    }

    setProcessingPayment(true);
    proceedWithPayment();
  };

  const proceedWithPayment = async (phoneOverride = null) => {
    try {
      const phoneToUse = phoneOverride || user.phoneNumber;
      console.log('💳 Proceeding with payment, phone:', phoneToUse);
      
      // Create order
      const orderResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.uid,
          'X-User-Email': user.email
        },
        body: JSON.stringify({
          planId: planDetails.planId,
          memberCount: planDetails.memberCount || 1,
          couponCode: appliedCoupon?.couponCode || null
        })
      });

      const orderData = await orderResponse.json();

      if (!orderResponse.ok) {
        throw new Error(orderData.detail || 'Failed to create order');
      }

      // Close our modal FIRST before loading Razorpay
      setOpen(false);
      setProcessingPayment(false);

      // Load Razorpay script if not already loaded
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // Wait for modal to fully unmount from DOM
      await new Promise(resolve => setTimeout(resolve, 100));

      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Piper Card',
        description: planDetails.name,
        order_id: orderData.orderId,
        retry: {
          enabled: true,
          max_count: 3
        },
        handler: async function (response) {
          // Verify payment
          try {
            const verifyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-User-Id': user.uid
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                paymentId: orderData.paymentId
              })
            });

            const verifyData = await verifyResponse.json();

            if (verifyResponse.ok && verifyData.success) {
              toast({
                title: "Payment Successful! 🎉",
                description: "Your subscription is now active. Redirecting to analytics...",
              });
              // Redirect to profile page with success state
              setTimeout(() => {
                window.location.href = '/profile?payment=success';
              }, 1500);
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            console.error('Verification error:', error);
            toast({
              title: "Payment Verification Failed",
              description: "Please contact support",
              variant: "destructive"
            });
          }
        },
        prefill: {
          email: user.email,
          ...(phoneToUse && { contact: String(phoneToUse).replace(/\D/g, '').slice(-10) })
        },
        theme: {
          color: '#9333EA'
        },
        modal: {
          ondismiss: function() {
            console.log('Razorpay dismissed');
          },
          escape: true,
          backdropclose: false,
          confirm_close: false,
          animation: true
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();

    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: "Payment Failed",
        description: error.message || "Something went wrong",
        variant: "destructive"
      });
      setProcessingPayment(false);
    }
  };

  if (!planDetails) return null;

  const finalAmount = appliedCoupon ? appliedCoupon.finalAmount : planDetails.price * 100;

  const handleClose = () => {
    if (processingPayment) {
      toast({
        title: "Payment in Progress",
        description: "Please wait for payment to complete",
        variant: "default"
      });
      return;
    }
    setOpen(false);
    setCouponCode('');
    setAppliedCoupon(null);
  };

  const handlePhoneDialogClose = () => {
    if (!savingPhone) {
      setShowPhoneDialog(false);
      setPhoneNumber('');
    }
  };

  return (
    <>
      {/* Custom Payment Modal - No Radix Dialog */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/80 animate-in fade-in-0"
            onClick={handleClose}
          />
          
          {/* Modal Content */}
          <div 
            ref={modalRef}
            className="relative z-50 w-full max-w-[500px] mx-4 bg-white rounded-lg shadow-lg animate-in fade-in-0 zoom-in-95"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
            >
              <XIcon className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>

            {/* Header */}
            <div className="flex flex-col space-y-1.5 p-6 pb-0 text-center sm:text-left">
              <h2 className="text-2xl font-bold leading-none tracking-tight">
                Complete Your Purchase
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Plan Summary */}
              <div className="bg-gradient-to-r from-amber-50 to-teal-50 rounded-lg p-4 border border-amber-200">
                <h3 className="font-semibold text-lg mb-2">{planDetails.name}</h3>
                <div className="flex items-baseline gap-2">
                  {appliedCoupon && (
                    <span className="text-gray-500 line-through text-sm">₹{planDetails.price}</span>
                  )}
                  <span className="text-3xl font-bold text-amber-600">
                    ₹{(finalAmount / 100).toFixed(2)}
                  </span>
                </div>
                {appliedCoupon && (
                  <Badge className="mt-2 bg-green-500 text-white">
                    Saved ₹{(appliedCoupon.discountAmount / 100).toFixed(2)}
                  </Badge>
                )}
              </div>

              {/* Coupon Section */}
              <div className="space-y-3">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Have a coupon code?
                </label>
                
                {appliedCoupon ? (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="flex-1 font-medium text-green-800">
                      {appliedCoupon.couponCode} applied
                    </span>
                    <button
                      onClick={removeCoupon}
                      className="text-red-600 hover:text-red-700"
                    >
                      <XIcon className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={applyCoupon}
                      disabled={loading || !couponCode.trim()}
                      variant="outline"
                      className="border-amber-600 text-amber-600 hover:bg-amber-50"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Payment Button */}
              <Button
                type="button"
                onClick={handlePayment}
                disabled={processingPayment}
                className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white"
                data-testid="pay-now-button"
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Pay ₹${(finalAmount / 100).toFixed(2)}`
                )}
              </Button>

              {/* Secure Payment Notice */}
              <p className="text-xs text-center text-gray-500">
                🔒 Secure payment powered by Razorpay
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Phone Number Collection Modal - Also Custom */}
      {showPhoneDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/80 animate-in fade-in-0"
            onClick={handlePhoneDialogClose}
          />
          
          {/* Modal Content */}
          <div className="relative z-50 w-full max-w-md mx-4 bg-white rounded-lg shadow-lg animate-in fade-in-0 zoom-in-95">
            {/* Header */}
            <div className="flex flex-col space-y-1.5 p-6 pb-0 text-center sm:text-left">
              <h2 className="text-xl font-bold text-gray-900 leading-none tracking-tight">
                Enter Your Phone Number
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                We need your phone number to process the payment and send you important updates.
              </p>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  placeholder="Enter 10-digit phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  maxLength={10}
                  className="w-full"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Format: 10 digits without country code
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePhoneDialogClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSavePhoneAndProceed}
                  disabled={savingPhone || phoneNumber.replace(/\D/g, '').length !== 10}
                  className="flex-1 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white"
                >
                  {savingPhone ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save & Continue'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentModal;
